import { z } from "zod";

import { idNameSchema } from "./generic-schema.ts";

// subSchemas //
const foundCarrierLtlInfoSchema = z.object({
	displayName: z.string(),
	isPreferred: z.boolean(),
	isEconomy: z.boolean(),
	isActive: z.boolean(),
	isContractRateOnly: z.boolean().nullable(),
});

export const chargeTypeSchema = z.object({
	id: z.number(),
	chargeType: z.string(),
	code: z.string(),
	p44Code: z.string().nullable(),
});

export const priceChargeSchema = z.object({
	id: z.number(),
	carrierPriceId: z.number(),
	sortOrder: z.number(),
	accessorialId: z.number().nullable(),
	chargeTypeId: z.number(),
	isCustomerCharge: z.boolean(),
	description: z.string().nullable(),
	amount: z.number(),
	rate: z.number().nullable(),
	rateQualifier: z.string().nullable(),
	quantity: z.number().nullable(),
	weight: z.number().nullable(),
	dimWeight: z.number().nullable(),
	freightClass: z.string().nullable(),
	fakFreightClass: z.string().nullable(),
	isMin: z.boolean(),
	isMax: z.boolean(),
	isNonTaxable: z.boolean(),
	chargeType: chargeTypeSchema,
});

export const carrierSchema = z.object({
	id: z.number(),
	name: z.string(),
	displayName: z.string().nullable(),
	tollFreeNumber: z.string(),
	isPreferred: z.boolean(),
	isCarrierOfTheYear: z.boolean(),
	isEconomy: z.boolean(),
	isPartials: z.boolean(),
	scac: z.string().nullable(),
});

// GET request Schemas //

// Endpoint: /api/carriers?nameBeginsWith={:userInput}&onlyLTL={:ltlFlag}&excludeNoLoadCarriersForCustomerId={:customerId}
export const foundCarrierSchema = z.object({
	id: z.number(),
	afterHoursPhone: z.string(),
	ciScore: z.number(),
	city: z.string(),
	dotNumber: z.string(),
	gpId: z.number(),
	hasDeliveredFirstLoad: z.boolean(),
	hasDispatcherOkForFuelStatus: z.boolean(),
	hasDriverOkForFuelStatus: z.boolean(),
	hasIMDLCarrierStatus: z.boolean(),
	hasLTLCarrierStatus: z.boolean(),
	hasNationalCarrierStatus: z.boolean(),
	intrastateCarrierFlag: z.boolean(),
	ltlInfo: foundCarrierLtlInfoSchema,
});

// Endpoint: /api/carrier-selection/insurance-markup?legacyId={:legacyId}&carrierId={:carrierId}
export const foundCarrierInsuranceMarkup = z.object({
	insuranceMarkup: z.number(),
	maxLiabilityOverride: z.number().nullable(),
	maxLiabilityNew: z.number(),
	maxLiabilityUsed: z.number(),
});

// Endpoint (internal): /api/carrier-selection/quotes/:quoteId/calculate-insurance-markups
// Endpoint (external): /api/carrier-selection/customerquotes/:quoteId/calculate-insurance-markups
export const calculateInsuranceMarkupsSchema = z.object({
	carrierMarkups: z.array(
		z.object({
			id: z.number(),
			markup: z.number(),
		}),
	),
});

export const carrierPriceSchema = z.object({
	id: z.number(),
	quoteId: z.number(),
	carrierId: z.number(),
	carrier: carrierSchema,
	carrierRate: z.number(),
	customerRate: z.number(),
	distributorRate: z.number().nullable(),
	brokerMargin: z.number(),
	insuranceMarkup: z.number().nullable().optional(),
	carrierQuoteId: z.string().nullable(),
	carrierQuoteExpirationDate: z.string(),
	carrierPriceTypeId: z.number(),
	carrierPriceType: idNameSchema.nullable(),
	serviceLevelId: z.number(),
	serviceLevel: idNameSchema,
	serviceAreaTypeId: z.number().nullable(),
	serviceType: z.string(),
	serviceLevelDescription: z.string(),
	destinationTerminalCode: z.string().nullable(),
	originTerminalCode: z.string().nullable(),
	transitDays: z.number().nullable(),
	maxLiabilityOverride: z.number().nullable(),
	maxLiabilityNew: z.number(),
	maxLiabilityUsed: z.number(),
	eTag: z.string().optional(),
	carrierTotalAccessorialCharges: z.number().nullable(),
	carrierTotalAccessorialFuelSurcharges: z.number().nullable(),
	customerTotalAccessorialCharges: z.number().nullable(),
	customerTotalAccessorialFuelSurcharges: z.number().nullable(),
	generatedPartialsRate: z.boolean().optional(),
	priceCharges: z.array(priceChargeSchema).nullable(),
	markedUpCustomerRate: z.number().nullable().optional(),
	originalBrokerMargin: z.number().optional(),
	isAdHoc: z.boolean().optional(),
	routingGuideApplied: z.boolean().optional(),
	routingGuideEvaluatedName: z.string().nullable().optional(),
});

export const customerCarrierPriceSchema = carrierPriceSchema
	.extend({
		carrierRate: z.number().nullish(),
		brokerMargin: z.number().nullish(),
	})
	.transform(({ carrierRate, brokerMargin, ...carrierPrice }) => ({
		...carrierPrice,
		carrierRate: carrierRate ?? 0,
		brokerMargin: brokerMargin ?? 0,
	}));

// PUT request Schema //
// Endpoint: /api/carrier-selection/7518775/select-carrier-price
// Endpoint: api/carrier-selection/quotes/7519422/add-insurance
export const selectCarrierSchema = z.object({
	id: z.number().optional(),
	eTag: z.string(),
	selectedCarrierPriceId: z.number().nullable(),
	insuranceApplyTypeId: z.number().nullable(),
	insuranceProductTypeId: z.number().nullable(),
	insuranceValue: z.number().nullable(),
});

export type CarrierPrice = z.infer<typeof carrierPriceSchema>;
export type Carrier = z.infer<typeof carrierSchema>;
export type SelectedCarrier = z.infer<typeof selectCarrierSchema>;
export type PriceCharge = z.infer<typeof priceChargeSchema>;
export type ChargeType = z.infer<typeof chargeTypeSchema>;
export type FoundCarrier = z.infer<typeof foundCarrierSchema>;
